import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { $pres } from 'strophe.js';
import { useAuth } from './AuthContext';

export type XMPPStatus = 'online' | 'away' | 'dnd' | 'offline';

interface XMPPContextType {
  status: XMPPStatus;
  setStatus: (status: XMPPStatus) => void;
  setStropheConn: (conn: any | null) => void;
}

const XMPPContext = createContext<XMPPContextType | undefined>(undefined);

export const XMPPProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatusState] = useState<XMPPStatus>('online');
  const connRef = useRef<any>(null);
  const { isAuthenticated } = useAuth();

  const sendPresence = (newStatus: XMPPStatus) => {
    const conn = connRef.current;
    if (!conn) return;
    try {
      if (newStatus === 'online') {
        conn.send($pres());
      } else if (newStatus === 'offline') {
        conn.send($pres({ type: 'unavailable' }));
      } else {
        // away | dnd
        conn.send($pres().c('show').t(newStatus));
      }
    } catch (e) {
      console.error('[XMPPContext] Failed to send presence:', e);
    }
  };

  const setStatus = (newStatus: XMPPStatus) => {
    setStatusState(newStatus);
    sendPresence(newStatus);
  };

  const setStropheConn = (conn: any | null) => {
    connRef.current = conn;
    if (conn && isAuthenticated) {
      // Announce initial presence on connect
      sendPresence(status);
    }
  };

  // Send unavailable when user logs out
  useEffect(() => {
    if (!isAuthenticated && connRef.current) {
      try { connRef.current.send($pres({ type: 'unavailable' })); } catch (_) {}
    }
  }, [isAuthenticated]);

  return (
    <XMPPContext.Provider value={{ status, setStatus, setStropheConn }}>
      {children}
    </XMPPContext.Provider>
  );
};

export const useXMPP = () => {
  const context = useContext(XMPPContext);
  if (context === undefined) {
    throw new Error('useXMPP must be used within an XMPPProvider');
  }
  return context;
};
